import {Component, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, Validators} from "@angular/forms";
import {environment} from "../../../environments/environment";
import {CreateUserRequest, RegistrationRequest, UserService} from "../../api";
import {ApiHelperService} from "../../services/api-helper-service/api-helper.service";
import {catchError, debounceTime, distinctUntilChanged, firstValueFrom, Subject, switchMap, takeUntil, throwError} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {HttpClient} from "@angular/common/http";
import {Plan} from "../../components/payment-table/payment-table.component";
import {MatStepper} from "@angular/material/stepper";
import {MatomoService} from "../../services/matomo/matomo.service";
import {EMPTY} from "rxjs";

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrl: './registration.component.scss'
})
export class RegistrationComponent implements OnDestroy, OnInit {

  @ViewChild('stepper') stepper!: MatStepper;
  loading = false;

  private readonly onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      this.loading = false;
    }
  };
  readonly version = environment.registrationVersion;
  hidePassword = true;
  hideConfirmPassword = true;
  emailAlreadyExists = false;
  passwordsMatch: boolean | null = null;
  private lastEmailAvailabilityCheck?: { email: string; available: boolean };

  // ── Step 2: Branchen + Templates aus API ────────────────────────────────
  industryCards: IndustryCard[] = [];
  templateCatalog: TemplateCard[] = [];
  private industryTemplateMapping: Record<string, string[]> = {};
  private industriesLoaded = false;

  // ── Kontextueller linker Bereich (Step 2) ───────────────────────────────
  // Keys matchen ConfigIndustry.Key (UPPERCASE)
  private readonly industryContextMap: Record<string, IndustryContext> = {
    _default: {
      title: 'So funktioniert Docuty',
      steps: [
        { icon: 'play_circle',   label: 'Einsatz starten',    description: 'Formular öffnen und direkt loslegen – auch ohne Internetverbindung.' },
        { icon: 'photo_camera',  label: 'Vor Ort erfassen',   description: 'Fotos, Texte und Daten strukturiert dokumentieren.' },
        { icon: 'picture_as_pdf',label: 'Protokoll erstellen', description: 'Fertiges Dokument automatisch generieren und versenden.' },
      ],
    },
    CONSTRUCTION: {
      title: 'So läuft deine Baustellendokumentation',
      steps: [
        { icon: 'engineering',    label: 'Begehung starten',        description: 'Baustelle auswählen und Formular öffnen – auch offline vor Ort.' },
        { icon: 'report_problem', label: 'Mängel dokumentieren',    description: 'Fotos aufnehmen, Mängel beschreiben und Maßnahmen festhalten.' },
        { icon: 'picture_as_pdf', label: 'Protokoll erstellen',     description: 'Fertig formatiertes Bauprotokoll oder Mängelbericht exportieren.' },
      ],
      hint: 'Passende Vorlagen für Baustellenbegehung und Abnahmen werden automatisch vorgeschlagen.',
    },
    CRAFTSMANSHIP: {
      title: 'So dokumentierst du deinen Einsatz',
      steps: [
        { icon: 'handyman',              label: 'Auftrag starten',      description: 'Kunden und Auftrag wählen, Formular direkt vor Ort öffnen.' },
        { icon: 'build',                 label: 'Arbeit dokumentieren', description: 'Erledigte Arbeiten, Materialien und Fotos festhalten.' },
        { icon: 'assignment_turned_in',  label: 'Abnahme erstellen',    description: 'Unterschrift einholen und Protokoll sofort per Mail versenden.' },
      ],
      hint: 'Vorlagen für Aufmaß, Abnahme und Mängeldokumentation stehen bereit.',
    },
    FACILITY_MANAGEMENT: {
      title: 'So läuft deine Objektkontrolle ab',
      steps: [
        { icon: 'apartment',  label: 'Objekt aufrufen',         description: 'Gebäude oder Bereich auswählen und Kontrolltour starten.' },
        { icon: 'checklist',  label: 'Prüfpunkte abarbeiten',   description: 'Zustand erfassen, Mängel markieren, Fotos direkt anfügen.' },
        { icon: 'summarize',  label: 'Bericht generieren',      description: 'Kontrolldokumentation sofort fertig – bereit zum Versand.' },
      ],
      hint: 'Vorlagen für Objektkontrolle, Reinigungsprüfung und Wartung werden vorgeschlagen.',
    },
    REAL_ESTATE: {
      title: 'So läuft deine Übergabe ab',
      steps: [
        { icon: 'key',          label: 'Übergabe starten',       description: 'Objekt aufrufen und Übergabeprotokoll öffnen.' },
        { icon: 'home_work',    label: 'Räume erfassen',         description: 'Zählerstände, Raumzustand und Fotos strukturiert dokumentieren.' },
        { icon: 'picture_as_pdf', label: 'Protokoll abschließen', description: 'Unterschrift einholen und fertiges Dokument sofort versenden.' },
      ],
      hint: 'Übergabe- und Abnahmevorlagen für Wohnungen und Gewerbeimmobilien inklusive.',
    },
    INDUSTRY: {
      title: 'So läuft deine Sicherheitsprüfung ab',
      steps: [
        { icon: 'factory',        label: 'Prüfung starten',      description: 'Anlage oder Bereich auswählen und Prüfformular öffnen.' },
        { icon: 'verified_user',  label: 'Befunde erfassen',     description: 'Prüfpunkte abarbeiten, Abweichungen markieren, Fotos dokumentieren.' },
        { icon: 'summarize',      label: 'Bericht abschließen',  description: 'Prüfprotokoll automatisch erstellen und revisionssicher archivieren.' },
      ],
      hint: 'Sicherheits- und Wartungsvorlagen für Industrie und Produktion sind vorausgewählt.',
    },
    EXPERT: {
      title: 'So erstellst du dein Gutachten-Protokoll',
      steps: [
        { icon: 'gavel',          label: 'Begehung starten',        description: 'Objekt aufrufen und Gutachtenformular öffnen – auch offline.' },
        { icon: 'photo_camera',   label: 'Befunde dokumentieren',   description: 'Schäden, Maße und Fotos lückenlos und strukturiert erfassen.' },
        { icon: 'picture_as_pdf', label: 'Protokoll exportieren',   description: 'Strukturiertes Dokument für Gutachten und Berichte generieren.' },
      ],
      hint: 'Vorlagen für Schadensdokumentation, Objektprüfung und Begehungen stehen bereit.',
    },
    OTHER: {
      title: 'So funktioniert Docuty in deinem Alltag',
      steps: [
        { icon: 'play_circle',    label: 'Einsatz starten',   description: 'Formular aufrufen und direkt mit der Erfassung beginnen.' },
        { icon: 'edit_note',      label: 'Daten erfassen',    description: 'Fotos, Texte und Informationen strukturiert dokumentieren.' },
        { icon: 'picture_as_pdf', label: 'Dokument erstellen', description: 'Fertiges Protokoll auf Knopfdruck exportieren und teilen.' },
      ],
    },
  };

  get currentIndustryContext(): IndustryContext {
    const key = this.selectedIndustry ?? '_default';
    return this.industryContextMap[key] ?? this.industryContextMap['_default'];
  }

  get selectedIndustryLabel(): string {
    if (!this.selectedIndustry) return 'Einsatzbereich';
    return this.industryCards.find(c => c.id === this.selectedIndustry)?.label ?? 'Einsatzbereich';
  }

  // ── URL-Parameter-Vorauswahl ─────────────────────────────────────────────
  preselectedBillingParam?: 'monthly' | 'yearly';
  preselectedPlanParam?: 'basic' | 'professional';
  /**
   * DOCU-1050: Herkunft aus dem ?src=-Parameter, wie sie beim Aufruf der Seite
   * ankam. Einmal beim Laden gelesen und festgehalten — der Stepper arbeitet
   * ohne Routenwechsel, aber ein spaeteres Lesen wuerde bei jeder kuenftigen
   * Navigation still leer werden.
   */
  private signupSourceParam?: string;

  // ── Step-2-State ────────────────────────────────────────────────────────
  selectedIndustry: string | null = null;
  selectedTemplates = new Set<string>();

  private destroy$ = new Subject<void>();

  userForm = this.fb.group({
    organisationDisplayName: ['', Validators.required],
    firstname: ['', Validators.required],
    lastname: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(/^(?=.*[0-9]).{8,}$/)]],
    confirmPassword: ['', Validators.required],
    industry: ['']
  });

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private userService: UserService,
    private apiHelper: ApiHelperService,
    private router: Router,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private matomo: MatomoService
  ) {
    this.userForm.get('password')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updatePasswordMatchState());

    this.userForm.get('confirmPassword')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updatePasswordMatchState());

    this.userForm.get('email')?.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(600),
      distinctUntilChanged(),
      switchMap(email => {
        this.emailAlreadyExists = false;
        this.lastEmailAvailabilityCheck = undefined;
        this.clearEmailExistsError();
        if (!email || this.userForm.get('email')?.hasError('email')) {
          return EMPTY;
        }
        return this.userService.checkEmailEndpoint(email).pipe(
          catchError(() => EMPTY)
        );
      })
    ).subscribe({
      next: (res) => {
        const email = this.userForm.get('email')?.value;
        if (email) {
          this.lastEmailAvailabilityCheck = {
            email,
            available: !!res.available
          };
        }
        if (!res.available) {
          this.setEmailExistsError();
        }
      }
    });
  }

  ngOnInit(): void {
    this.loadIndustries();
    window.addEventListener('pageshow', this.onPageShow);
    this.readUrlParams();
    this.matomo.trackStep(0);
  }

  /**
   * DOCU-1035: Schrittwechsel im Stepper melden. Der Stepper ist der einzige
   * Ort, an dem ein Abbruch ueberhaupt sichtbar werden kann — wer aufgibt,
   * sendet nichts mehr, und der zuletzt gemeldete Schritt ist der
   * Absprungpunkt.
   */
  onStepChange(index: number): void {
    this.matomo.trackStep(index);
  }

  private readUrlParams(): void {
    const params = this.route.snapshot.queryParamMap;
    const plan = params.get('plan')?.toLowerCase();
    const billing = params.get('billing')?.toLowerCase();
    if (plan === 'basic' || plan === 'professional') {
      this.preselectedPlanParam = plan;
    }
    if (billing === 'monthly' || billing === 'yearly') {
      this.preselectedBillingParam = billing;
    }
    // DOCU-1035: Herkunft aus den Marketing-CTAs (DOCU-1036 setzt ?src= auf
    // allen 29 Knoepfen). Wird nur gemeldet, nicht ausgewertet — die
    // Interpretation gehoert nach Matomo, nicht ins Formular.
    const src = params.get('src');
    this.matomo.trackSource(src);

    // DOCU-1050: dieselbe Herkunft geht zusaetzlich an die API und landet dort an
    // der Organisation (DOCU-1051). Matomo zaehlt Aufrufe, auch die von
    // Abbrechern; die Spalte an der Organisation beantwortet die andere Frage —
    // welcher Kanal einen zahlenden Kunden gebracht hat. Beides wird gebraucht.
    //
    // Hier absichtlich ohne Pruefung uebernommen: was ein brauchbarer Wert ist,
    // entscheidet die API an einer Stelle (SignupSourceNormalizer). Eine
    // zweite Regel im Formular wuerde beim naechsten neuen src-Wert auseinander
    // laufen. Gekuerzt wird trotzdem, damit nichts Unbegrenztes losgeschickt wird.
    this.signupSourceParam = src?.trim().slice(0, 100) || undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('pageshow', this.onPageShow);
  }

  private loadIndustries(): void {
    this.http.get<IndustryApiResponse[]>(`${environment.apiBaseUrl}/api/v1/registration/industries`)
      .subscribe({
        next: (industries) => {
          this.industryCards = industries.map(i => ({
            id: i.key,
            label: i.label,
            icon: i.icon
          }));

          // Build template catalog from all unique templates across industries
          const templateMap = new Map<string, TemplateCard>();
          const mapping: Record<string, string[]> = {};

          for (const industry of industries) {
            mapping[industry.key] = industry.recommendedTemplates.map(t => t.id);
            for (const t of industry.recommendedTemplates) {
              if (!templateMap.has(t.id)) {
                templateMap.set(t.id, {
                  id: t.id,
                  name: t.name,
                  description: t.description ?? '',
                  icon: 'description'
                });
              }
            }
          }

          this.templateCatalog = Array.from(templateMap.values());
          this.industryTemplateMapping = mapping;
          this.industriesLoaded = true;
        },
        error: () => {
          // Fallback: leere Listen – Step 2 zeigt keine Branchen/Templates
          this.industriesLoaded = true;
        }
      });
  }

  updatePasswordMatchState(): void {
    const password = this.userForm.get('password')?.value;
    const confirmPassword = this.userForm.get('confirmPassword');

    if (!confirmPassword?.value) {
      this.passwordsMatch = null;
      return;
    }

    if (password !== confirmPassword.value) {
      this.passwordsMatch = false;
      confirmPassword.setErrors({ ...confirmPassword.errors, passwordMismatch: true });
    } else {
      this.passwordsMatch = true;
      const errors = { ...confirmPassword.errors };
      delete errors['passwordMismatch'];
      confirmPassword.setErrors(Object.keys(errors).length > 0 ? errors : null);
    }
  }

  togglePasswordVisibility() {
    this.hidePassword = !this.hidePassword;
  }

  toggleConfirmPasswordVisibility() {
    this.hideConfirmPassword = !this.hideConfirmPassword;
  }

  get isAccountStepValid(): boolean {
    return !!(
      this.userForm.controls.organisationDisplayName.valid &&
      this.userForm.controls.firstname.valid &&
      this.userForm.controls.lastname.valid &&
      this.userForm.controls.email.valid &&
      this.userForm.controls.password.valid &&
      this.userForm.controls.confirmPassword.valid &&
      this.passwordsMatch === true
    );
  }

  get isProfileStepValid(): boolean {
    return true;
  }

  private setEmailExistsError(): void {
    this.emailAlreadyExists = true;
    const emailControl = this.userForm.get('email');
    if (!emailControl) {
      return;
    }

    emailControl.setErrors({ ...emailControl.errors, emailExists: true });
    emailControl.markAsTouched();
  }

  private clearEmailExistsError(): void {
    const emailControl = this.userForm.get('email');
    if (!emailControl?.errors?.['emailExists']) {
      return;
    }

    const errors = { ...emailControl.errors };
    delete errors['emailExists'];
    emailControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
  }

  useDifferentEmail(): void {
    this.emailAlreadyExists = false;
    this.lastEmailAvailabilityCheck = undefined;
    this.userForm.get('email')?.reset('');
    this.clearEmailExistsError();
  }

  goToStep(index: number): void {
    this.ngZone.run(() => {
      this.stepper.selectedIndex = index;
    });
  }

  async onAccountStepContinue(): Promise<void> {
    this.userForm.controls.organisationDisplayName.markAsTouched();
    this.userForm.controls.firstname.markAsTouched();
    this.userForm.controls.lastname.markAsTouched();
    this.userForm.controls.email.markAsTouched();
    this.userForm.controls.password.markAsTouched();
    this.userForm.controls.confirmPassword.markAsTouched();
    if (
      this.userForm.controls.organisationDisplayName.invalid ||
      this.userForm.controls.firstname.invalid ||
      this.userForm.controls.lastname.invalid ||
      this.userForm.controls.email.invalid ||
      this.userForm.controls.password.invalid ||
      this.userForm.controls.confirmPassword.invalid ||
      this.passwordsMatch !== true
    ) {
      return;
    }

    const email = this.userForm.get('email')?.value;
    if (email) {
      try {
        const cachedResult = this.lastEmailAvailabilityCheck;
        const res = cachedResult?.email === email
          ? cachedResult
          : await firstValueFrom(this.userService.checkEmailEndpoint(email));

        this.lastEmailAvailabilityCheck = {
          email,
          available: !!res?.available
        };

        if (!res?.available) {
          this.setEmailExistsError();
          return;
        }
      } catch {
      }
    }

    this.goToStep(1);
  }

  onProfileStepContinue(): void {
    this.goToStep(2);
  }

  onSubmit(plan: Plan) {
    const raw = this.userForm.getRawValue();
    const basePlan = this.getBasePlan(plan.priceId);
    // DOCU-1041: Die returnUrl wird ausschließlich von Stripe aufgerufen. Sie trägt deshalb
    // immer source=stripe — unabhängig davon, was hier angefordert wurde.
    //
    // Vorher stand hier der *angeforderte* Zustand (source=trial bei Basic/Pro). Das war
    // falsch: Läuft dieses Frontend gegen eine API ohne DOCU-1040, wird startTrial ignoriert
    // und es kommt doch ein Checkout zurück. Stripe ruft dann die returnUrl auf, und die
    // Erfolgsseite hätte einem Kunden, der gerade bezahlt hat, "kostenlose Testphase, es wird
    // nichts abgebucht" angezeigt.
    const returnUrl = `${window.location.origin}/registration/success?plan=${encodeURIComponent(basePlan)}&source=stripe`;
    const req = {
      organisationDisplayName: raw.organisationDisplayName,
      returnUrl,
      selectedPlanLookup: plan.priceId,
      startTrial: plan.startsTrial === true,
      industryKey: this.selectedIndustry ?? undefined,
      selectedTemplateIds: this.selectedTemplates.size > 0
        ? Array.from(this.selectedTemplates)
        : undefined,
      signupSource: this.signupSourceParam,
      createUserRequest: {
        email: raw.email,
        firstname: raw.firstname,
        lastname: raw.lastname,
        password: raw.password
      } as CreateUserRequest
    } as RegistrationRequest;

    if (plan.requiresCheckout) {
      this.loading = true;
    }

    this.userService.registrationEndpoint(req).pipe(
      catchError((e) => {
        const langKey = e?.error?.langKey;
        if (langKey === 'email_exists' || e?.error?.error === 'email_exists') {
          this.setEmailExistsError();
          this.goToStep(0);
          this.loading = false;
          return throwError(() => e);
        }
        this.apiHelper.onError(e);
        return throwError(() => e);
      })
    ).subscribe({
      next: value => {
        if (value.checkout !== undefined && value.checkout != null) {
          window.location.href = value.checkout;
        } else {
          // Was tatsächlich passiert ist, entscheidet die Antwort der API — nicht, was
          // hier angefordert wurde. Ohne trialEndsAt gab es keine Testphase, egal ob
          // startTrial gesendet wurde (ältere API kennt das Feld nicht).
          this.router.navigate(['/registration/success'], {
            queryParams: {
              plan: basePlan,
              source: value.trialEndsAt ? 'trial' : 'direct',
              ...(value.trialEndsAt ? { trialEndsAt: value.trialEndsAt } : {})
            }
          });
        }
      },
      error: () => {
        if (!this.emailAlreadyExists) {
          this.goToStep(2);
        }
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  // ── Step-2-Logik ────────────────────────────────────────────────────────

  selectIndustry(id: string): void {
    if (this.selectedIndustry === id) return;   // kein Reset bei gleicher Branche
    this.selectedIndustry = id;
    this.userForm.patchValue({ industry: id });
    // Beim Branchenwechsel empfohlene Templates automatisch vorauswählen
    this.selectedTemplates = new Set(this.industryTemplateMapping[id] ?? []);
  }

  toggleTemplate(id: string): void {
    if (this.selectedTemplates.has(id)) {
      this.selectedTemplates.delete(id);
    } else {
      this.selectedTemplates.add(id);
    }
  }

  isTemplateSelected(id: string): boolean {
    return this.selectedTemplates.has(id);
  }

  isRecommendedTemplate(id: string): boolean {
    if (!this.selectedIndustry) return false;
    return (this.industryTemplateMapping[this.selectedIndustry] ?? []).includes(id);
  }

  get sortedTemplates(): TemplateCard[] {
    // Empfohlene Templates erscheinen zuerst
    const recommended = this.templateCatalog.filter(t => this.isRecommendedTemplate(t.id));
    const others = this.templateCatalog.filter(t => !this.isRecommendedTemplate(t.id));
    return [...recommended, ...others];
  }

  private getBasePlan(priceId: string): string {
    return priceId.replace('_YEARLY', '');
  }

  get passwordChecks(): { label: string; valid: boolean }[] {
    const password = this.userForm.controls.password.value || '';
    return [
      { label: 'REGISTER.PASSWORD_REQUIREMENT_LENGTH', valid: password.length >= 8 },
      { label: 'REGISTER.PASSWORD_REQUIREMENT_NUMBER', valid: /\d/.test(password) },
      { label: 'REGISTER.PASSWORD_REQUIREMENT_MATCH', valid: this.passwordsMatch === true }
    ];
  }
}

// ── API-Response-Modell ───────────────────────────────────────────────────
interface IndustryApiResponse {
  id: number;
  key: string;
  label: string;
  icon: string;
  recommendedTemplates: { id: string; name: string; description?: string; catalogKey?: string; }[];
}

// ── View-Modelle für Schritt 2 ────────────────────────────────────────────
export interface IndustryCard {
  id: string;       // Industry Key (z.B. "CONSTRUCTION")
  label: string;
  icon: string;
}

export interface TemplateCard {
  id: string;       // FormTemplate GUID
  name: string;
  description: string;
  icon: string;
}

export interface IndustryContext {
  title: string;
  steps: { icon: string; label: string; description: string; }[];
  hint?: string;
}
